/**
 * Centralised API helpers.
 * ────────────────────────
 * Build tag: 2026-06-15-vapid-rotate   ← invalidates Emergent build cache
 *
 * EXPORTS
 *   • `API`     – base URL string (`<origin>/api`). Use it in legacy
 *                 callsites that already do `axios.get(`${API}/...`)`.
 *   • `api`     – preconfigured axios instance. New code SHOULD prefer
 *                 this and write paths *relative to /api*, e.g.
 *                 `api.get("/admin/settings/chat-message-limit")`.
 *                 Both `api` and the default global axios share the same
 *                 cookie-based session — they're equivalent at the
 *                 network layer.
 *   • `installAuthRedirect()` – installs a 401 response interceptor on
 *                 the default `axios` AND on the `api` instance.
 *                 Call once from App.js. On 401 the user is redirected
 *                 to `/login` (with `?next=<current path>`) and a toast
 *                 explains the session expired. Public/auth callsites
 *                 are exempted (see PUBLIC_ROUTES + EXEMPT_PATHS).
 *
 * WHY THIS FILE EXISTS
 *   Two production bugs we never want to repeat:
 *     1. URL doubling: `${process.env.REACT_APP_BACKEND_URL}/api` when the
 *        env var lacks `https://` → axios treats it as relative → URL
 *        becomes `https://startip.es/startip.es/api/...` (HTTP 405).
 *     2. Stale 401s leaving users on broken pages because no global
 *        handler kicked them back to /login.
 *   Centralising here makes both impossible to reintroduce.
 */
import axios from "axios";

/* ── 1. Single source of truth for the base URL ────────────────────────── */
// `window.location.origin` is always correctly formatted in the browser.
// We fall back to the env var only for SSR / build-time contexts.
export const API =
  (typeof window !== "undefined"
    ? window.location.origin
    : process.env.REACT_APP_BACKEND_URL || "") + "/api";

/* ── 2. Preconfigured axios instance for new code ──────────────────────── */
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 15000,
});

/* ── 3. 401 redirect interceptor ───────────────────────────────────────── */
// Routes where a 401 should NOT trigger a redirect — the user is already
// on a public page (or callback) and the page handles the missing-session
// case itself.
const PUBLIC_ROUTES = [
  "/login",
  "/register",
  "/",
  "/access-denied",
  "/auth/callback",
  "/google-drive/callback",
  "/aviso-legal",
  "/politica-privacidad",
  "/politica-cookies",
  "/canal/",   // Canal público — accesible sin autenticación
  "/tip/",     // Perfil de modelo — accesible sin autenticación
];

// API endpoints that legitimately return 401 as part of normal flow —
// we must NOT hijack them.
const EXEMPT_API_PATHS = [
  "/auth/me",              // bootstrap probe, AuthContext handles silently
  "/auth/login",           // wrong password → expected 401
  "/auth/google",          // Google session probe
  "/me/heartbeat-active",  // background heartbeat, should not trigger logout
  "/me/self-service-token",       // optional feature probe; non-fatal 401
  "/me/self-service-credentials", // one-time token; 403 is expected when expired
  "/consumer/dashboard",   // dashboard data; handled locally, must not loop
  "/premium/drive/",       // Drive token expiry should not log user out
  "/premium/content/",     // Drive-backed content; token expiry shows component-level error
];

function isExemptUrl(url) {
  if (!url) return false;
  let path;
  try {
    path = new URL(url, window.location.origin).pathname;
  } catch {
    path = String(url);
  }
  return EXEMPT_API_PATHS.some((p) => path.endsWith(p) || path.includes(p));
}

function isOnPublicRoute() {
  if (typeof window === "undefined") return true;
  const here = window.location.pathname;
  return PUBLIC_ROUTES.some(
    (r) => here === r || (r !== "/" && here.startsWith(r + "/")),
  );
}

let redirectInFlight = false;
function handle401(error) {
  // Only kick to /login once per error storm — if 12 requests fail in
  // parallel we still navigate exactly once.
  if (redirectInFlight) return;
  if (isOnPublicRoute()) return;
  if (isExemptUrl(error?.config?.url)) return;

  redirectInFlight = true;
  // Persist where the user was so we can bring them back after login.
  const here =
    window.location.pathname + window.location.search + window.location.hash;
  const nextParam =
    here && here !== "/" ? `?next=${encodeURIComponent(here)}` : "";
  // Use a soft window.location nav (full reload) — we don't have direct
  // access to react-router's navigate() from here and a hard nav also
  // guarantees the in-memory user state is cleared.
  window.location.assign(`/login${nextParam}`);
}

let installed = false;
export function installAuthRedirect() {
  if (installed) return;
  installed = true;

  const onReject = (error) => {
    if (error?.response?.status === 401) handle401(error);
    return Promise.reject(error);
  };

  axios.interceptors.response.use((r) => r, onReject);
  api.interceptors.response.use((r) => r, onReject);
}

export default api;
